import { OrderDictionaryType } from "modules/orders-manage/models/order-dictionary"
import { UserTimesheetType } from "modules/spending/timesheet/models/user-timesheet"
import React, { useCallback } from "react"
import styles from "./DepartmentBlock.module.scss"
import { MonthUserRow, MonthUserRowEditor } from "./MonthUserRow"
import { DaySelector, TimesheetCellMode } from "./TimesheetCell"
import { TimesheetSaver } from "./TimesheetInput"
import { TimesheetUserRow } from "./TimesheetUserRow"
import { WeekDays, toWeekDayViews } from "./WeekDays"

import { Collapse } from "@blueprintjs/core"
import { Caret } from "modules/common/components/collapse/Caret"
import { Peoples } from "modules/orders-manage/details/Peoples"
import { TasksDictionaryType, WorkloadDayType } from "modules/spending/workload/models/workload-day"
import { observer } from "mobx-react"
import { DepartmentBlockMode, TimesheetStoreType } from "../models/timesheet-store"

/**
 * Список сотрудников отдела берётся из стора, а не приходит пропом.
 *
 * Раньше сюда передавался массив узлов MST из вычисляемого `departmentMap` —
 * а он пересобирается заново при каждом пересчёте, то есть проп каждый раз был
 * новым объектом. В dev-сборке React 19 сменившийся проп-объект разбирается по
 * свойствам для вкладки «Components» профайлера; обход узлов MST поднимает их
 * ленивые поля вне фазы инициализации, MST бросает проверку прямо в фазе
 * коммита, и после этого приложение перестаёт перерисовываться при переходах.
 *
 * Стор — тоже узел MST, но его тождество неизменно, поэтому внутрь React не
 * заходит. Компонент стал observer, чтобы самому подписаться на вычисляемое.
 */
export const DepartmentBlock: React.FC<DepartmentBlockProps> = observer((props) => {
    const { collapsed, toggleCollapse, onEditStateChanged } = props
    const { department, store, cellMode, mode, tasks, selectedCells, selectedOrderId, onSelectCells, onContext, ...selector } = props

    const users: UserTimesheetType[] = store.departmentMap[department] ?? []

    const sortedUsers = [...users].sort((a, b) => {
        const nameA = a?.user.name || ''
        const nameB = b.user.name || ''
        return nameA.localeCompare(nameB)
    })


    const onToggleCollapse = useCallback(() => toggleCollapse(department), [toggleCollapse, department])

    return (
        <div className={`${styles.block} department-block`}>
            <h1 className="planr-block-header-wide_lowscreen collapser" onClick={onToggleCollapse}>
                <div className={styles.departmentName}>
                    <div className={styles.department}>{department}</div>

                    <Peoples count={users.length} />
                </div>
                {!collapsed && (
                    <>
                        {mode === "day" && (
                            <div style={{ marginLeft: "-306px" }}>
                                {users.length && <WeekDays days={toWeekDayViews(users[0].days)} />}
                            </div>
                        )}
                    </>
                )}
                <Caret collapsed={collapsed} style={{ left: "7px" }} />
            </h1>
            <Collapse isOpen={!collapsed} keepChildrenMounted={true}>
                <div className="collapse-wide_lowscreen" style={{ paddingTop: "0px" }}>
                    {mode === "day" && (
                        <>
                            {/* {users.length && <WeekDays days={toWeekDayViews(users[0].days)} />} */}
                            {sortedUsers.map((timesheet) => (
                                <TimesheetUserRow
                                    timesheet={timesheet}
                                    key={timesheet.user.id}
                                    mode={cellMode}
                                    tasks={tasks}
                                    selectedCells={selectedCells}
                                    onSelectCells={onSelectCells}
                                    onContext={onContext}
                                    selectedOrderId={selectedOrderId}
                                    {...selector}
                                />
                            ))}
                        </>
                    )}
                    {mode === "month" && (
                        <>
                            {/* {users.length && <MonthHeader />} */}
                            {users.map((timesheet) => (
                                <MonthUserRow
                                    timesheet={timesheet}
                                    key={timesheet.user.id}
                                    onEditStateChanged={onEditStateChanged}
                                    {...selector}
                                />
                            ))}
                        </>
                    )}
                </div>
            </Collapse>
        </div>
    )
})

interface DepartmentBlockProps extends DaySelector, TimesheetSaver, MonthUserRowEditor {
    department: string
    store: TimesheetStoreType
    orders: OrderDictionaryType
    mode: DepartmentBlockMode
    canManage: boolean
    userLogin: string
    cellMode: TimesheetCellMode
    collapsed: boolean
    toggleCollapse: (key: string) => void
    tasks: TasksDictionaryType
    showMinutes: boolean
    onContext: (x: number, y: number, day: WorkloadDayType) => void
    selectedCells: string[]
    onSelectCells: (cell: string[]) => void
    selectedOrderId?: string
}
